import { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { onAuthStateChanged, signInAnonymously } from "firebase/auth";
import { auth } from "./firebase";
import { getSongs } from "./api";
import SongList from "./components/SongList";
import Player from "./components/Player";
import AdminLogin from "./components/AdminLogin";
import AdminDashboard from "./components/AdminDashboard";
import "./App.css";

const GCS = "https://storage.googleapis.com/myaimusic";
const ALLOWED = process.env.REACT_APP_ADMIN_EMAIL;

// ── Admin page (protected) ──────────────────────────────────────────────────

function AdminPage() {
  const [user, setUser]         = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u && u.email === ALLOWED) setUser(u);
      else setUser(null);
      setChecking(false);
    });
    return unsub;
  }, []);

  if (checking) return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", fontFamily: "'DM Sans', sans-serif",
      fontSize: 11, color: "#ccc", letterSpacing: ".1em", textTransform: "uppercase",
    }}>
      Checking auth...
    </div>
  );

  if (!user) return <AdminLogin onLogin={setUser} />;
  return <AdminDashboard user={user} />;
}

// ── Main site ───────────────────────────────────────────────────────────────

function MainApp() {
  const [songs, setSongs]         = useState([]);
  const [current, setCurrent]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [queue, setQueue]         = useState([]);
  const [queueOpen, setQueueOpen] = useState(false);
  const [userId, setUserId]       = useState(null);

  // Anonymous auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) {
        setUserId(u.uid);
      } else {
        signInAnonymously(auth).catch(console.warn);
      }
    });
    return unsub;
  }, []);

  // Load songs
  useEffect(() => {
    getSongs().then(setSongs).finally(() => setLoading(false));
  }, []);

  const addToQueue = useCallback((song) => {
    setQueue(q => q.find(s => s.id === song.id) ? q : [...q, song]);
  }, []);

  const removeFromQueue = useCallback((id) => {
    setQueue(q => q.filter(s => s.id !== id));
  }, []);

  const playNext = useCallback((currentSong) => {
    if (queue.length > 0) {
      const next = queue[0];
      setQueue(q => q.slice(1));
      setCurrent(next);
      return;
    }
    const same = songs.filter(s =>
      s.genre === currentSong.genre && s.id !== currentSong.id
    );
    const pool = same.length ? same : songs;
    const next = pool[Math.floor(Math.random() * pool.length)];
    setCurrent(next);
  }, [queue, songs]);

  return (
    <div className="app">
      <div className="header">
        <img
          className="logo-img"
          src={`${GCS}/cover_art/snhw_logo.png`}
          alt="Songs No Human Wrote"
        />
        <p className="header-sub">100% AI Generated · Lyria 3</p>
      </div>

      {loading ? (
        <p className="loading">loading tracks...</p>
      ) : (
        <SongList
          songs={songs}
          current={current}
          onSelect={setCurrent}
          onAddToQueue={addToQueue}
          userId={userId}
        />
      )}

      {current && (
        <Player
          song={current}
          songs={songs}
          queue={queue}
          onSelect={setCurrent}
          onNext={playNext}
          onAddToQueue={addToQueue}
          onRemoveFromQueue={removeFromQueue}
          queueOpen={queueOpen}
          onToggleQueue={() => setQueueOpen(o => !o)}
        />
      )}

      <div className="footer">
        <p>
          Music born from artificial imagination with guidance from a human.<br />
          <p>@SandraCalvo</p>
        </p>
      </div>
    </div>
  );
}

// ── Root with router ────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/"      element={<MainApp />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </BrowserRouter>
  );
}